import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getAuth } from "firebase/auth";
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { ThemeProvider } from "next-themes";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./components/layout/Sidebar";
import { Toaster as Sonner } from "./components/ui/sonner";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import { app } from "./firebase";
import { LoadingProvider } from "./hooks/useLoading";
import Certificates from "./pages/Certificates";
import Chat from "./pages/Chat";
import CourseDetails from "./pages/CourseDetails";
import Courses from "./pages/Courses";
import CreateCourse from "./pages/CreateCourse";
import Dashboard from "./pages/Dashboard";
import EditCourse from "./pages/EditCourse";
import Files from "./pages/FIles";
import GenerateCertificate from "./pages/GenerateCertificate";
import IDCard from "./pages/IDCard";
import Index from "./pages/Index";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import Notification from "./pages/Notification";
import Profile from "./pages/Profile";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Students from "./pages/Students";
import Users from "./pages/Users";
import AuthRequests from "./pages/admin/AuthRequests";
import CreateUser from "./pages/admin/CreateUser";
import IdCardWaitingList from "./pages/admin/IdCardWaitingList";
import ManageAccessRoles from "./pages/admin/ManageAccessRoles";
import ManageLmsAccessAdmin from "./pages/admin/ManageLmsAccessAdmin";
import ResetUserPassword from "./pages/admin/ResetUserPassword";
import AddOrEditBatch from "./pages/batches/AddOrEditBatch";
import AllBatches from "./pages/batches/AllBatches";
import AllEmployees from "./pages/employees/AllEmployees";
import EmployeeOffboarding from "./pages/employees/EmployeeOffboarding";
import EmployeeOnboarding from "./pages/employees/EmployeeOnboarding";
import EmployeePayslips from "./pages/employees/EmployeePayslips";
import EmployeeSalary from "./pages/employees/EmployeeSalary";
import RegisterEmployee from "./pages/employees/RegisterEmployee";
import ContactusRequests from "./pages/enquires/ContactUsRequests";
import DriveRequests from "./pages/enquires/DriveRequests";
import EnquiryRequests from "./pages/enquires/EnquiryRequests";
import OfferRegistrations from "./pages/enquires/OfferRegistrations";
import SyllabusRequests from "./pages/enquires/SyllabusRequests";
import LmsWaitingList from "./pages/lms/LmsWaitingList";
import ManageLmsAccess from "./pages/lms/ManageLmsAccess";
import ProvideNewAccess from "./pages/lms/ProvideNewLmsAccess";
import AccessRequests from "./pages/logs/AccessRequests";
import CourseRegistrations from "./pages/logs/CourseRegistrations";
import DraftApprovals from "./pages/logs/DraftApprovals";
import DraftSubmissions from "./pages/logs/DraftSubmissions";
import InformationLogins from "./pages/logs/InformationLogins";
import LmsLogins from "./pages/logs/LmsLogins";
import RegistrationLogins from "./pages/logs/RegistrationLogins";
import CheckStudentsData from "./pages/students/CheckStudentsData";
import DraftList from "./pages/students/DraftList";
import DraftRegistration from "./pages/students/DraftRegistration";
import FeeList from "./pages/students/FeeList";
import StudentRegistration from "./pages/students/StudentRegistration";
import ScriptInjector from "./pages/ScriptInjector";
import AddEditModule from "./pages/modules/AddEditModule";
import ExternalStudents from "./pages/ExternalStudents";
import CareerGapRegistrations from "./pages/enquires/CareerGapRegistrations";
import FilesDashboard from "./pages/files/FilesDashboard";

const queryClient = new QueryClient();


// PrivateRoute component to handle protected routes  
const PrivateRoute = ({ element, isLoggedIn }) => {  
  return isLoggedIn ? element : <Navigate to="/login" />;  
};  

function App() {  
  const [isLoggedIn, setIsLoggedIn] = useState(false);  
  const [isLoading, setIsLoading] = useState(true);  
  const db = getFirestore(app);  
  const auth = getAuth(app);  
  const navigate = useNavigate();  
  const location = useLocation();  

  useEffect(() => {  
    const checkUserLoggedIn = async () => {  
      const userData = JSON.parse(localStorage.getItem('user')) ?? null;  

      // Allow access if route is '/' or empty  
      if (location.pathname === '/' || location.pathname === '') {  
        setIsLoading(false); // Skip authentication check  
        return; // No need to navigate anywhere  
      }  

      if (userData && userData?.uid && userData.tokenCreatedAt) {  
        try {  
          const uid = userData.uid;  
          const usersRef = collection(db, 'users');  
          const q = query(usersRef, where("uid", "==", uid));  
          const querySnapshot = await getDocs(q);  

          if (!querySnapshot.empty) {  
            setIsLoggedIn(true);  
            setIsLoading(false);
            navigate(location.pathname);
          } else {
            setIsLoading(false);
            console.error("No user record found in Firestore");  
          }  
        } catch (error) {  
          setIsLoading(false);
          console.error("Error during authentication:", error);  
        }  
      }  
      else {
        localStorage.clear();
      }
      setIsLoading(false);  
    };  

    checkUserLoggedIn();  
  }, [db, auth]);  

  // Redirect if not logged in and trying to access protected routes  
  useEffect(() => {  
    if (!isLoading &&
       !isLoggedIn && location.pathname !== '/login'
      && location.pathname !== '/' && location.pathname !== '/register-draft') {  
      navigate("/login");  
    }  
  }, [isLoggedIn, isLoading, navigate]);  

  if (isLoading) {  
    return <div>Loading...</div>; // You can replace this with a loading spinner or similar component  
  } 

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <LoadingProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ScriptInjector />
        <Toaster />
        <Sonner />
        <ScrollToTop />
        <div className="flex h-auto w-full overflow-hidden">
          {isLoggedIn ? <Sidebar isLoggedIn={isLoggedIn} /> : ''}
          <main className="flex-1 flex flex-col overflow-hidden">
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/register-draft"  element={<DraftRegistration />}  />
              <Route path="/courses/create" element={<CreateCourse />} />
              <Route path="/courses/:courseId" element={<CourseDetails />} />
              <Route path="/courses/edit/:courseId" element={<EditCourse />} />
              <Route path="/modules/create" element={<AddEditModule />} />
              <Route path="/modules/edit/:moduleId" element={<AddEditModule />} />
              <Route path="/dashboard" element={<PrivateRoute element={<Dashboard />} isLoggedIn={isLoggedIn} />} />
              <Route path="/login" element={<Login />} />
              <Route path="/courses" element={<PrivateRoute element={<Courses />} isLoggedIn={isLoggedIn} />} />
              <Route path="/courses/create" element={<PrivateRoute element={<CreateCourse />} isLoggedIn={isLoggedIn} />} />
              <Route path="/courses/:courseId" element={<PrivateRoute element={<CourseDetails />} isLoggedIn={isLoggedIn} />} />
              <Route path="/courses/edit/:courseId" element={<PrivateRoute element={<EditCourse />} isLoggedIn={isLoggedIn} />} />
              <Route path="/file/files" element={<PrivateRoute element={<Files />} isLoggedIn={isLoggedIn} />} />
              <Route path="/file/filesdashboard" element={<PrivateRoute element={<FilesDashboard />} isLoggedIn={isLoggedIn} />} />
              <Route path="/enquiries/requests" element={<PrivateRoute element={<EnquiryRequests />} isLoggedIn={isLoggedIn} />} />
              <Route path="/enquiries/drive" element={<PrivateRoute element={<DriveRequests />} isLoggedIn={isLoggedIn} />} />
              <Route path="/enquiries/offers" element={<PrivateRoute element={<OfferRegistrations />} isLoggedIn={isLoggedIn} />} />
              <Route path="/enquiries/syllabus" element={<PrivateRoute element={<SyllabusRequests />} isLoggedIn={isLoggedIn} />} />
              <Route path="/enquiries/contactus" element={<PrivateRoute element={<ContactusRequests />} isLoggedIn={isLoggedIn} />} />
               <Route path="/enquiries/careergapregistrations" element={<PrivateRoute element={<CareerGapRegistrations />} isLoggedIn={isLoggedIn} />} />
              
              <Route path="/students" element={<PrivateRoute element={<Students />} isLoggedIn={isLoggedIn} />} />
              <Route path="/students/register" element={<PrivateRoute element={<StudentRegistration />} isLoggedIn={isLoggedIn} />} />
              
              <Route path="/students/external-list" element={<PrivateRoute element={<ExternalStudents />} isLoggedIn={isLoggedIn} />} />
              <Route path="/students/draft-list" element={<PrivateRoute element={<DraftList />} isLoggedIn={isLoggedIn} />} />
              <Route path="/students/fee-list" element={<PrivateRoute element={<FeeList />} isLoggedIn={isLoggedIn} />} />
              <Route path="/students/check-data" element={<PrivateRoute element={<CheckStudentsData />} isLoggedIn={isLoggedIn} />} />
              <Route path="/employees" element={<PrivateRoute element={<AllEmployees />} isLoggedIn={isLoggedIn} />} />
              <Route path="/employees/register" element={<PrivateRoute element={<RegisterEmployee />} isLoggedIn={isLoggedIn} />} />
              <Route path="/employees/onboarding" element={<PrivateRoute element={<EmployeeOnboarding />} isLoggedIn={isLoggedIn} />} />
              <Route path="/employees/payslips" element={<PrivateRoute element={<EmployeePayslips />} isLoggedIn={isLoggedIn} />} />
              <Route path="/employees/salary" element={<PrivateRoute element={<EmployeeSalary />} isLoggedIn={isLoggedIn} />} />
              <Route path="/employees/offboarding" element={<PrivateRoute element={<EmployeeOffboarding />} isLoggedIn={isLoggedIn} />} />
              <Route path="/logs/lms" element={<PrivateRoute element={<LmsLogins />} isLoggedIn={isLoggedIn} />} />
              <Route path="/logs/registration" element={<PrivateRoute element={<RegistrationLogins />} isLoggedIn={isLoggedIn} />} />
              <Route path="/logs/information" element={<PrivateRoute element={<InformationLogins />} isLoggedIn={isLoggedIn} />} />
              <Route path="/logs/access" element={<PrivateRoute element={<AccessRequests />} isLoggedIn={isLoggedIn} />} />
              <Route path="/logs/courses" element={<PrivateRoute element={<CourseRegistrations />} isLoggedIn={isLoggedIn} />} />
              <Route path="/logs/submissions" element={<PrivateRoute element={<DraftSubmissions />} isLoggedIn={isLoggedIn} />} />
              <Route path="/logs/approvals" element={<PrivateRoute element={<DraftApprovals />} isLoggedIn={isLoggedIn} />} />
              <Route path="/id-card" element={<PrivateRoute element={<IDCard />} isLoggedIn={isLoggedIn} />} />
              <Route path="/lms/waiting-list" element={<PrivateRoute element={<LmsWaitingList />} isLoggedIn={isLoggedIn} />} />
              <Route path="/lms/manage-access" element={<PrivateRoute element={<ManageLmsAccess />} isLoggedIn={isLoggedIn} />} />
              <Route path="/lms/provide-access" element={<PrivateRoute element={<ProvideNewAccess />} isLoggedIn={isLoggedIn} />} />
              <Route path="/admin/create-user" element={<PrivateRoute element={<CreateUser />} isLoggedIn={isLoggedIn} />} />
              <Route path="/admin/manage-access" element={<PrivateRoute element={<ManageAccessRoles />} isLoggedIn={isLoggedIn} />} />
              <Route path="/admin/lms-access" element={<PrivateRoute element={<ManageLmsAccess />} isLoggedIn={isLoggedIn} />} />
              <Route path="/admin/reset-password" element={<PrivateRoute element={<ResetUserPassword />} isLoggedIn={isLoggedIn} />} />
              <Route path="/admin/auth-requests" element={<PrivateRoute element={<AuthRequests />} isLoggedIn={isLoggedIn} />} />
              <Route path="/admin/id-card-waiting" element={<PrivateRoute element={<IdCardWaitingList />} isLoggedIn={isLoggedIn} />} />
              <Route path="/users" element={<PrivateRoute element={<Users />} isLoggedIn={isLoggedIn} />} />
              <Route path="/reports" element={<PrivateRoute element={<Reports />} isLoggedIn={isLoggedIn} />} />
              <Route path="/settings" element={<PrivateRoute element={<Settings />} isLoggedIn={isLoggedIn} />} />
              <Route path="/notification" element={<PrivateRoute element={<Notification />} isLoggedIn={isLoggedIn} />} />
              <Route path="/certificates" element={<PrivateRoute element={<Certificates />} isLoggedIn={isLoggedIn} />} />
              <Route path="/certificates/generate" element={<PrivateRoute element={<GenerateCertificate />} isLoggedIn={isLoggedIn} />} />
              <Route path="/batches" element={<PrivateRoute element={<AllBatches />} isLoggedIn={isLoggedIn} />} />
              <Route path="/batches/create" element={<PrivateRoute element={<AddOrEditBatch />} isLoggedIn={isLoggedIn} />} />
              <Route path="/batches/edit/:batchId" element={<PrivateRoute element={<AddOrEditBatch />} isLoggedIn={isLoggedIn} />} />
              <Route path="/inbox" element={<PrivateRoute element={<Chat />} isLoggedIn={isLoggedIn} />} />
              <Route path="/chat" element={<PrivateRoute element={<Chat />} isLoggedIn={isLoggedIn} />} />
              <Route path="/profile" element={<PrivateRoute element={<Profile />} isLoggedIn={isLoggedIn} />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </div>
      </TooltipProvider>
    </QueryClientProvider>
    </LoadingProvider>
    </ThemeProvider>
  );
}

export default App;  





const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};